import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Upload, FileText, Trash2, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsForm = ({ applicationId, onSave }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('Resume');
  const [documentReviewConsent, setDocumentReviewConsent] = useState(false);

  useEffect(() => {
    if (applicationId) loadDocuments();
  }, [applicationId]);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/applications/${applicationId}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);

      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/documents`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast({ title: 'Success', description: 'Document uploaded successfully' });
      loadDocuments();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">Upload supporting documents (Resume, Portfolio, etc.)</p>
      </div>

      {/* Document Review Permission */}
      <Card className="p-6 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <Shield className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-amber-900 mb-2">Document Review Authorization</h4>
            <p className="text-sm text-amber-800 mb-4">
              To ensure the authenticity and accuracy of your application, we may need to review your documents. 
              This review may be conducted by Wayo authorized professionals, our AI verification system, 
              or trusted third-party services solely for authentication and verification purposes.
            </p>
            <div className="flex items-start gap-3">
              <Checkbox 
                id="document-consent" 
                checked={documentReviewConsent}
                onCheckedChange={setDocumentReviewConsent}
                data-testid="document-review-consent-checkbox"
              />
              <Label htmlFor="document-consent" className="text-sm text-amber-900 leading-relaxed cursor-pointer">
                I authorize Wayo and its authorized professionals, AI systems, or third-party verification services 
                to review my uploaded documents for authentication and verification purposes only.
              </Label>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <Label>Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Resume">Resume</SelectItem>
                <SelectItem value="Portfolio">Portfolio</SelectItem>
                <SelectItem value="Transcript">Transcript</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleUpload} className="hidden" id="doc-upload" />
            <label htmlFor="doc-upload" className="cursor-pointer">
              <p className="text-gray-700 font-medium">Click to upload</p>
              <p className="text-sm text-gray-500">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
            </label>
          </div>
        </div>
      </Card>

      {documents.length > 0 && (
        <Card className="p-6">
          <h4 className="font-semibold mb-4">Uploaded Documents</h4>
          <div className="space-y-2">
            {documents.map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <p className="font-medium">{doc.file_name}</p>
                    <p className="text-sm text-gray-600">{doc.document_type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={() => onSave(documents)}>Save & Continue</Button>
      </div>
    </div>
  );
};

export default DocumentsForm;